import { Component, EventEmitter, OnInit, Renderer2, ChangeDetectionStrategy } from '@angular/core';
import {
  PgTitleComponent,
  PgToolbarComponent,
} from '../../../shared/components/title-bar/title-bar.component';
import _ from 'lodash';
import { FreshwaterToolbarComponent } from './fresh-water-toolbar/fresh-water-toolbar.component';
import {
  OrganisationService,
} from 'src/app/organisation/organisation.service';
import { forkJoin } from 'rxjs';
import { Router } from '@angular/router';
import saveAs from 'file-saver';
import {
  MessageLevel,
  PgMessagesService,
} from 'src/app/shared/services/pg-messages.service';
import { DocumentService } from 'src/app/documents/documents.service';
import { DateTimeHelpers } from 'src/app/shared/helpers/date-time.helpers';
import { SharedModule } from 'src/app/shared/shared.module';
import { AgGridModule } from 'ag-grid-angular';
import { GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AgGridService } from 'src/app/shared/ag-grid/ag-grid.service';
import { UserLocalStoreService } from 'src/app/shared/services/user-local-store.service';
import { FreshwaterDeliveryNote } from 'src/app/generated/services/transportation/reports/freshwater';
import { FreshwaterServiceSpa } from 'src/app/generated/services/transportation/reports/freshwaterSpa';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { LuxonDateTimeHelpers } from 'src/app/shared/helpers/luxon-date-time.helpers';

@Component({
  selector: 'app-fresh-waters',
  templateUrl: 'fresh-waters.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports:[SharedModule, AgGridModule],
})
export class FreshWatersComponent
implements OnInit, PgTitleComponent, PgToolbarComponent {
  title: EventEmitter<string> = new EventEmitter();
  freshWatersDeliveryNote: FreshwaterDeliveryNote[] = [];

  gridOptions: GridOptions = {};
  gridRestored: boolean = false;

  biggestSupplierProvidedTonnage!: number;
  biggestSupplierOrganisationId!: string;
  organisations!: Map<string, Organisation>;
  numberOfSuppliers!: number;
  numberOfReceivingLocations!: number;
  numberOfFreshWaters!: number;

  toolbarComponentType = FreshwaterToolbarComponent;
  toolbarComponent: any;

  constructor(
    private _organisationservice: OrganisationService,
    private _router: Router,
    private _documentsService: DocumentService,
    private _pgMessagesService: PgMessagesService,
    private _renderer: Renderer2,
    private _freshwaterServiceSpa: FreshwaterServiceSpa,
    private _userLocalStorage: UserLocalStoreService,

  ) { }

  columnDefs = [
    {
      headerName: 'Actions',
      cellRenderer: (params: { data: { fileId: string; vesselName: any; bdnNumber: any; }; }) => {
        const container = this._renderer.createElement('div');

        if (params.data.fileId) {
          const downloadBtn = AgGridService.downloadButton(
            this._renderer,
            () => this.downloadFile(params.data.fileId, `${params.data.vesselName}-${params.data.bdnNumber}`),
          );
          this._renderer.appendChild(container, downloadBtn);
        }

        const editBtn = AgGridService.editButton(
          this._renderer,
          this._router,
          (_router, data) => this.editRecord(data),
          params.data
        );
        this._renderer.appendChild(container, editBtn);

        return container;
      }
    },
    {headerName:'Fresh Water Delivery Note NO.', field:'bdnNumber'},
    {headerName:'Vessel', field:'vesselName'},
    {headerName:'Supplier', field:'supplierOrganisationId',
      cellRenderer: (row: any) => this.formatOrganisation(row.value)
    },
    {headerName:'Location', field:'locationOrganisationId',
      cellRenderer: (row: any) => this.formatOrganisation(row.value)
    },
    {headerName:'Quantity', field:'quantity'},
    {headerName:'Date', field:'date',
      cellRenderer: (row: any) => LuxonDateTimeHelpers.offsetDateTimeToLocalString(row.data.date, 'yyyy-LL-dd')
    },
  ];

  ngOnInit(): void {
    this.gridOptions = AgGridService.getOptions();
    this.title.emit('Fresh waters deliveries');
    forkJoin([
      this._freshwaterServiceSpa.getAllFreshwaters({}),
      this._organisationservice.getOrganisationIdToOrganisationMap(),
    ]).subscribe((value) => {
      this.freshWatersDeliveryNote = value[0].freshWaters.sort((a, b) => new Date(b.date!.isoDate || '').getTime() - new Date(a.date!.isoDate || '').getTime());
      this.organisations = value[1];
      this.numberOfFreshWaters = this.freshWatersDeliveryNote.length;
      this.numberOfReceivingLocations = [
        ...new Set(
          this.freshWatersDeliveryNote.map((b) => b.locationOrganisationId)
        ),
      ].length;
      this.numberOfSuppliers = [
        ...new Set(
          this.freshWatersDeliveryNote.map((b) => b.supplierOrganisationId)
        ),
      ].length;
      const highest = _.chain(this.freshWatersDeliveryNote)
        .groupBy((b) => b.supplierOrganisationId)
        .map((v, k) => ({
          supplierOrganisationId: k,
          tons: v.reduce((a, b) => a + b.quantity, 0),
        }))
        .sort((a, b) => b.tons - a.tons)
        .value()[0];
      if (highest) {
        this.biggestSupplierOrganisationId = highest.supplierOrganisationId;
        this.biggestSupplierProvidedTonnage = highest.tons;
      }
    });
  }

  gridSetting(event: GridReadyEvent) {
    AgGridService.setting(event);
  }

  saveGridState(event: GridReadyEvent) {
    AgGridService.saveGridState(event, this.gridRestored, this._userLocalStorage, 'freshwaterGrid');
  }

  restoreGridState(event: GridReadyEvent) {
    this.gridRestored = AgGridService.restoreGridState(event, this._userLocalStorage, 'freshwaterGrid');
  }

  dateOnly(date: string){
    return DateTimeHelpers.formatDateString(date);
  }

  editRecord(freshwaterdn: FreshwaterDeliveryNote) {
    this._router.navigate([
      '/vessels/fresh-water',
      freshwaterdn.vesselImo,
      freshwaterdn.bdnNumber,
      freshwaterdn.date?.isoDate,
    ]);
  }

  downloadFile(fileId: string, prefix: string) {
    this._documentsService.download(fileId).subscribe(
      (b) => {
        saveAs(b.body, `${prefix}.pdf`);
      },
      () => {
        this._pgMessagesService.publishMessage({
          message: 'Could not download this file.',
          level: MessageLevel.Error,
          topic: 'Fresh Water document',
        });
      }
    );
  }

  formatOrganisation(organisationId: string) {
    const foundOrganisation = this.organisations?.get(organisationId);
    return foundOrganisation ? foundOrganisation.nameTranslations['en'] : '';
  }
}
